import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import LoadingScreen from '../components/shared/LoadingScreen';
import PublishModal from '../components/shared/PublishModal';
import type { BusinessPlan } from '../types/businessPlan';
import { logoColorToHex } from '../types/businessPlan';
import { buildSlides } from '../lib/pitchDeckSlides';

// ── Print / PDF export ────────────────────────────────────────────────────────

function buildPrintHTML(plan: BusinessPlan): string {
  const s1 = plan.stageResponses?.ideaExplosion;
  const s2 = plan.stageResponses?.problemSolver;
  const s4 = plan.stageResponses?.mvpMadness;
  const s5 = plan.stageResponses?.moneyMoves;

  const esc = (s?: string | number | null) =>
    s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const sections: { emoji: string; title: string; body: string }[] = [];

  sections.push({
    emoji: '📋',
    title: '',
    body: `<div style="text-align:center;padding:2rem 0">
      <h2 style="font-size:2rem;margin:0">${esc(plan.businessName || 'My Business')}</h2>
      <p style="color:#6b7280;margin-top:0.5rem">by ${esc(plan.explorerName)}</p>
      <p style="color:#d94f1e;font-size:0.85rem;text-transform:uppercase;letter-spacing:0.1em;margin-top:1rem">Business Plan</p>
    </div>`,
  });

  sections.push({
    emoji: '🔍',
    title: 'The Problem',
    body: `<h4>The Struggle Is Real</h4>
      ${s2?.s2_q1 ? `<p>${esc(s2.s2_q1)}</p>` : ''}
      ${s2?.s2_q2 ? `<p style="color:#6b7280;font-size:0.85rem">Pain level: ${esc(s2.s2_q2)}</p>` : ''}
      ${plan.problemDiaryEntries?.length ? '<p style="font-size:0.85rem;font-weight:600;color:#4b5563;margin-top:0.75rem">Evidence:</p><ul>' + plan.problemDiaryEntries.map(e => `<li>${esc(e.ughMoment)} <span style="color:#9ca3af">(${esc(e.location)})</span></li>`).join('') + '</ul>' : ''}`,
  });

  sections.push({
    emoji: '🌟',
    title: 'Our Solution',
    body: `<h4>${esc(plan.businessName || 'Our Product')}</h4>
      ${s1?.s1_q1 ? `<p style="color:#d94f1e;font-weight:600">${esc(s1.s1_q1)}</p>` : ''}
      ${s1?.s1_q2 ? `<p>${esc(s1.s1_q2)}</p>` : ''}
      ${s1?.s1_q3 ? `<p style="color:#6b7280;font-size:0.85rem">Friend hype: ${esc(s1.s1_q3)}/4</p>` : ''}`,
  });

  sections.push({
    emoji: '🕵️',
    title: 'Our Customer',
    body: `<h4>CEO Contract</h4>
      ${plan.ceoBigPromise ? `<p><strong>Our Promise:</strong> ${esc(plan.ceoBigPromise)}</p>` : ''}
      ${plan.ceoCustomerFeeling ? `<p><strong>How Customers Will Feel:</strong> ${esc(plan.ceoCustomerFeeling)}</p>` : ''}
      ${plan.ceoCeoOath ? `<p><strong>CEO Oath:</strong> <em>&ldquo;${esc(plan.ceoCeoOath)}&rdquo;</em></p>` : ''}
      ${s4?.s4_q1 ? `<p><strong>Target:</strong> ${esc(s4.s4_q1)}</p>` : ''}`,
  });

  sections.push({
    emoji: '✅',
    title: 'Validation',
    body: `<h4>People Want This</h4>
      ${plan.friendSurveyResponses?.length ? `<p><strong>${plan.friendSurveyResponses.length} people surveyed</strong></p><ul>` + plan.friendSurveyResponses.slice(0, 3).map(r => `<li>${esc(r.reaction)}</li>`).join('') + '</ul>' : ''}`,
  });

  const pc = plan.profitCalculation;
  const profitHTML = pc
    ? `<div style="background:#f0fdf4;padding:1rem;border-radius:0.75rem;margin:0.5rem 0"><p style="color:#166534;font-weight:600;font-size:0.85rem">Cost: ₹${esc(pc.costToMake)} → Sell: ₹${esc(pc.sellingPrice)} → Profit: ₹${esc(pc.sellingPrice - pc.costToMake)}</p></div>`
    : '';
  sections.push({
    emoji: '💰',
    title: 'Business Model',
    body: `<h4>The Money</h4>
      ${s5?.s5_q1 ? `<p><strong>Price range:</strong> ${esc(s5.s5_q1)}</p>` : ''}
      ${s5?.s5_q4 ? `<p><strong>Strategy:</strong> ${esc(s5.s5_q4)}</p>` : ''}
      ${profitHTML}`,
  });

  sections.push({
    emoji: '📊',
    title: 'The Pitch',
    body: `<h4>Elevator Pitch</h4>
      <div style="background:#fff8f5;padding:1.5rem;border-radius:0.75rem;border:1.5px solid #fcd9cc">
        ${plan.elevatorPitchWho ? `<p><strong>For</strong> ${esc(plan.elevatorPitchWho)}</p>` : ''}
        ${plan.elevatorPitchProblem ? `<p><strong>who struggle with</strong> ${esc(plan.elevatorPitchProblem)}</p>` : ''}
        ${plan.elevatorPitchSolution ? `<p><strong>we offer</strong> ${esc(plan.elevatorPitchSolution)}</p>` : ''}
        ${plan.elevatorPitchWhy ? `<p><strong>because</strong> ${esc(plan.elevatorPitchWhy)}</p>` : ''}
      </div>`,
  });

  if (plan.selectedLogoColors?.length || plan.selectedLogoShape) {
    const bgColor = plan.selectedLogoColors?.[0] ? logoColorToHex(plan.selectedLogoColors[0]) : '#6366f1';
    const borderColor = plan.selectedLogoColors?.[1] ? logoColorToHex(plan.selectedLogoColors[1]) : undefined;
    const shape = plan.selectedLogoShape === 'circle' ? '●' : plan.selectedLogoShape === 'star' ? '★' : plan.selectedLogoShape === 'diamond' ? '◆' : plan.selectedLogoShape === 'heart' ? '♥' : '■';
    sections.push({
      emoji: '🎨',
      title: 'Brand',
      body: `<div style="text-align:center">
        <h4>Brand Identity</h4>
        <div style="display:inline-flex;align-items:center;justify-content:center;width:8rem;height:8rem;border-radius:1rem;color:#fff;font-size:2.5rem;font-weight:700;background:${bgColor};${borderColor ? `border:4px solid ${borderColor};` : ''}box-shadow:0 4px 6px rgba(0,0,0,0.1)">${shape}</div>
        ${plan.logoKeywords ? `<p style="color:#6b7280;font-size:0.85rem;margin-top:0.75rem">Keywords: ${esc(plan.logoKeywords)}</p>` : ''}
      </div>`,
    });
  }

  const slidesHTML = sections
    .map(
      (s, i) => `<div class="slide">
        ${s.title ? `<div class="slide-header"><span>${s.emoji}</span> Slide ${i + 1} — ${esc(s.title)}</div>` : ''}
        ${s.body}
      </div>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${esc(plan.businessName || 'Pitch Deck')} — Pitch Deck</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111827; padding: 2rem; }
    h2 { font-size: 2rem; }
    h4 { font-size: 1.15rem; font-weight: 700; margin-bottom: 0.5rem; }
    p { margin: 0.35rem 0; line-height: 1.5; }
    ul { padding-left: 1.25rem; margin: 0.35rem 0; }
    li { margin: 0.2rem 0; font-size: 0.9rem; }
    .slide { border: 1px solid #e5e7eb; border-radius: 0.75rem; padding: 1.5rem 2rem; margin-bottom: 1.5rem; page-break-inside: avoid; break-inside: avoid; }
    .slide-header { font-size: 0.8rem; color: #9ca3af; margin-bottom: 0.75rem; }
    @media print {
      body { padding: 0; }
      .slide { border: none; border-bottom: 1px solid #e5e7eb; border-radius: 0; padding: 1rem 0; margin-bottom: 1rem; }
      .slide:last-child { border-bottom: none; }
    }
  </style>
</head>
<body>
  ${slidesHTML}
  <script>window.onafterprint = () => window.close();</script>
</body>
</html>`;
}

// ── Page component ────────────────────────────────────────────────────────────

export default function PitchDeckPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plan, loading } = useBusinessPlan(planId);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPublish, setShowPublish] = useState(false);

  const slides = plan ? buildSlides(plan) : [];

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!slides.length) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setCurrentSlide((p) => Math.min(slides.length - 1, p + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrentSlide((p) => Math.max(0, p - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [slides.length]);

  const handleExportPDF = useCallback(() => {
    if (!plan) return;
    const html = buildPrintHTML(plan);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to export the PDF.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }, [plan]);

  if (loading) return <LoadingScreen />;

  if (!plan) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <p className="text-gray-400">Plan not found</p>
      </div>
    );
  }

  const isFirst = currentSlide === 0;
  const isLast = currentSlide === slides.length - 1;

  return (
    <div className="flex min-h-screen flex-col bg-gray-950">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        className="flex shrink-0 items-center justify-between px-5 py-3"
        style={{ backgroundColor: '#111827', borderBottom: '1px solid #1f2937' }}
      >
        <button
          onClick={() => navigate(`/hq/${planId}`)}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          ← Back to HQ
        </button>

        <div className="text-center">
          <p className="text-sm font-semibold text-white">
            {plan.businessName || 'Pitch Deck'}
          </p>
          <p className="text-xs text-gray-500">
            {currentSlide + 1} of {slides.length}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowPublish(true)}
            className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-300 transition-colors hover:bg-white/10"
            style={{ borderColor: '#374151' }}
          >
            Share
          </button>
          <button
            onClick={handleExportPDF}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#d94f1e' }}
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* ── Slide viewer ───────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-4xl">

          {/* Slide with overlaid nav arrows */}
          <div className="relative">
            <div className="aspect-video w-full overflow-hidden rounded-2xl shadow-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="h-full w-full"
                >
                  {slides[currentSlide].content}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Prev arrow */}
            {!isFirst && (
              <button
                onClick={() => setCurrentSlide((p) => p - 1)}
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white transition-colors"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.55)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.35)')}
              >
                ←
              </button>
            )}

            {/* Next arrow */}
            {!isLast && (
              <button
                onClick={() => setCurrentSlide((p) => p + 1)}
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white transition-colors"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.55)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.35)')}
              >
                →
              </button>
            )}
          </div>

          {/* ── Thumbnail strip ──────────────────────────────────────────────── */}
          <div className="mt-5 flex justify-center gap-1 overflow-x-auto pb-1">
            {slides.map((slide, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className="flex shrink-0 flex-col items-center gap-1 rounded-xl px-3 py-2 transition-all"
                style={{
                  backgroundColor:
                    i === currentSlide ? 'rgba(255,255,255,0.1)' : 'transparent',
                  borderBottom:
                    i === currentSlide ? '2px solid #d94f1e' : '2px solid transparent',
                }}
              >
                <span className="text-base leading-none">{slide.emoji}</span>
                <span
                  className="max-w-[56px] truncate text-[10px] font-medium"
                  style={{ color: i === currentSlide ? '#e5e7eb' : '#6b7280' }}
                >
                  {slide.title}
                </span>
              </button>
            ))}
          </div>

          <p className="mt-3 text-center text-xs text-gray-600">
            Use ← → arrow keys to navigate
          </p>
        </div>
      </div>

      <PublishModal
        visible={showPublish}
        onClose={() => setShowPublish(false)}
        defaultContentType="pitch"
        defaultCaption={plan.elevatorPitchSolution || ''}
        planId={planId}
        businessName={plan.businessName}
      />
    </div>
  );
}
